/**
 * components/auth/LoginPage.tsx
 * ─────────────────────────────────────────────────────────────
 * Login page. Matches the dark terminal aesthetic of the dashboard.
 * Shows on first load and after session expiry.
 * ─────────────────────────────────────────────────────────────
 */

import { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';

interface LoginPageProps {
  onLogin: (username: string) => void;
  error?:  string;
}

export function LoginPage({ onLogin, error: externalError }: LoginPageProps) {
  const [username, setUsername]   = useState('');
  const [password, setPassword]   = useState('');
  const [loading,  setLoading]    = useState(false);
  const [error,    setError]      = useState<string | null>(externalError ?? null);
  const [shake,    setShake]      = useState(false);
  const [showPass, setShowPass]   = useState(false);
  const [dots,     setDots]       = useState('');
  const usernameRef               = useRef<HTMLInputElement>(null);

  // Blinking cursor dots animation for the title
  useEffect(() => {
    const id = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);
    return () => clearInterval(id);
  }, []);

  // Auto-focus username field
  useEffect(() => { usernameRef.current?.focus(); }, []);

  // Sync external error
  useEffect(() => { if (externalError) setError(externalError); }, [externalError]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!username.trim() || !password || loading) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/login', {
        method:      'POST',
        headers:     { 'Content-Type': 'application/json' },
        credentials: 'include',
        body:        JSON.stringify({ username: username.trim(), password }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Login failed');
        setShake(true);
        setPassword('');
        setTimeout(() => setShake(false), 600);
      } else {
        onLogin(data.data.username);
      }
    } catch {
      setError('Cannot connect to server. Check your connection.');
      setShake(true);
      setTimeout(() => setShake(false), 600);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="h-app overflow-y-auto bg-bg-0 flex flex-col p-4">
      {/* Subtle grid background */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(to right, #2ECC71 1px, transparent 1px),
            linear-gradient(to bottom, #2ECC71 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      <div
        className={clsx(
          'w-full max-w-sm m-auto relative animate-slide-in',
          shake && 'animate-[shake_0.5s_ease-in-out]'
        )}
        style={shake ? { animation: 'shake 0.5s ease-in-out' } : undefined}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="font-display text-5xl font-extrabold tracking-tight mb-2">
            PHASE<span className="text-bull">1</span>
          </div>
          <div className="text-xs font-mono text-ink-3 tracking-widest uppercase">
            Trading Dashboard{dots}
          </div>
        </div>

        {/* Login card */}
        <div className="bg-bg-1 border border-border rounded-xl overflow-hidden">

          {/* Card header bar */}
          <div className="bg-bg-2 border-b border-border px-5 py-3 flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-bear/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-amber/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-bull/60" />
            <span className="ml-2 text-2xs font-mono text-ink-3 tracking-widest">
              SECURE LOGIN
            </span>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">

            {/* Error message */}
            {error && (
              <div className="bg-bear/10 border border-bear/25 rounded-lg px-4 py-3 flex items-start gap-2.5">
                <span className="text-bear text-sm flex-shrink-0 mt-0.5">✗</span>
                <span className="text-bear text-xs font-mono leading-relaxed">{error}</span>
              </div>
            )}

            {/* Username */}
            <div>
              <label className="block text-2xs font-mono uppercase tracking-widest text-ink-3 mb-2">
                Username
              </label>
              <input
                ref={usernameRef}
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="Enter username"
                autoComplete="username"
                spellCheck={false}
                disabled={loading}
                className={clsx(
                  'w-full bg-bg-2 border rounded-md px-4 py-3',
                  'text-ink-1 text-sm font-mono placeholder:text-ink-3',
                  'outline-none transition-colors duration-150',
                  'disabled:opacity-50',
                  error
                    ? 'border-bear/40 focus:border-bear'
                    : 'border-border focus:border-bull'
                )}
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-2xs font-mono uppercase tracking-widest text-ink-3 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter password"
                  autoComplete="current-password"
                  disabled={loading}
                  className={clsx(
                    'w-full bg-bg-2 border rounded-md px-4 py-3 pr-12',
                    'text-ink-1 text-sm font-mono placeholder:text-ink-3',
                    'outline-none transition-colors duration-150',
                    'disabled:opacity-50',
                    error
                      ? 'border-bear/40 focus:border-bear'
                      : 'border-border focus:border-bull'
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink-2 transition-colors text-xs font-mono"
                  tabIndex={-1}
                >
                  {showPass ? 'HIDE' : 'SHOW'}
                </button>
              </div>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading || !username.trim() || !password}
              className={clsx(
                'w-full py-3 rounded-md text-sm font-mono font-medium',
                'transition-all duration-150 border',
                'disabled:opacity-40 disabled:cursor-not-allowed',
                !loading && username && password
                  ? 'bg-bull/10 border-bull/30 text-bull hover:bg-bull/18 active:scale-[0.99]'
                  : 'bg-bg-2 border-border text-ink-3'
              )}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-flex gap-1">
                    {[0, 0.15, 0.3].map((d, i) => (
                      <span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-bull/60 animate-pulse"
                        style={{ animationDelay: `${d}s` }}
                      />
                    ))}
                  </span>
                  Authenticating…
                </span>
              ) : (
                'Sign In →'
              )}
            </button>

          </form>

          {/* Footer */}
          <div className="border-t border-border px-5 py-3">
            <div className="text-2xs font-mono text-ink-3 text-center">
              Session expires after 12 hours · HttpOnly cookie
            </div>
          </div>
        </div>


      </div>

      {/* Shake keyframe */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%       { transform: translateX(-8px); }
          40%       { transform: translateX(8px); }
          60%       { transform: translateX(-5px); }
          80%       { transform: translateX(5px); }
        }
      `}</style>
    </div>
  );
}
